import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { PageExperience } from '@/features/experiences/types';
import { resolveWeekStartDay } from '@/features/layouts/weekStart.lib';
import {
  Calendar,
  type DayRenderer,
  type HeaderDayRenderer,
} from '@/ui/Calendar/Calendar';
import { weekStartToWeekday } from '@/utils/dates';
import type { GroupData, MonthData } from '../types';
import { toMonthKey } from '../util.lib';
import { DayCell } from './DayCell';
import { DayHeader } from './DayHeader';
import { GridContainer } from './GridContainer';
import { HeaderRow } from './HeaderRow';
import { WeekRow } from './WeekRow';

type Props = {
  monthData: MonthData;
  experience: PageExperience;
  pageIndex: number;
};

export const CalendarGrid = ({ monthData, experience, pageIndex }: Props) => {
  const {
    month: { month, year },
    boxes,
    weekRowRange,
  } = monthData;
  const monthKey = toMonthKey({ month, year });

  const boxesByDate = useMemo(() => {
    return boxes.reduce<Map<string, GroupData>>((map, box) => {
      const key = box.date.toISODate();
      if (key) {
        map.set(key, box);
      }
      return map;
    }, new Map());
  }, [boxes]);

  const renderDay: DayRenderer = (dayContext) => {
    return (
      <DayCell
        box={boxesByDate.get(dayContext.date)}
        dayContext={dayContext}
        experience={experience}
      />
    );
  };

  const renderHeaderDay: HeaderDayRenderer = (headerContext) => {
    return (
      <DayHeader dayOfWeek={headerContext.dayOfWeek} experience={experience} />
    );
  };

  const Container = ({ children }: { children: ReactNode }) => (
    <GridContainer pageIndex={pageIndex}>{children}</GridContainer>
  );

  const Header = ({ children }: { children: ReactNode }) => (
    <HeaderRow monthKey={monthKey}>{children}</HeaderRow>
  );

  const Week = ({
    children,
    weekIndex,
  }: {
    children: ReactNode;
    weekIndex?: number;
  }) => (
    <WeekRow
      monthKey={monthKey}
      weekIndex={weekIndex}
      weekRowRange={weekRowRange}
    >
      {children}
    </WeekRow>
  );

  return (
    <Calendar
      Container={Container}
      Header={Header}
      Week={Week}
      firstDayOfWeek={weekStartToWeekday(resolveWeekStartDay(experience))}
      month={month}
      renderDay={renderDay}
      renderHeaderDay={renderHeaderDay}
      year={year}
    />
  );
};
